// Export orchestrator. Components call `exportVoiceOnly(assetIdx)` /
// `exportMixed(assetIdx)` / `exportVideoMp4(assetIdx)` and get back a Promise;
// the hook returns a `status` string for the component to render next to
// the active button. A single mutex (`busy`) forbids concurrent exports.
//
// Wiring:
//   - Reads `config.assets` + `customAssets` + `assetBlocks` from the session
//     store to assemble the voice-clip list (each block's favorite take).
//   - Reads `mixer` for Mixed + Video paths.
//   - Resolves the video source from `useVideoStore` (local blob URL) or the
//     admin `asset.videoPath` via `streamVideoUrl(toolId, videoPath)`.
//   - Uses `videoInputSize` to decide streaming vs. in-memory MP4 output.
//
// Filenames (legacy `_exportFilenameBase`): `{nr}_{title}_{suffix}.{ext}`
// with non-alnum stripped from title and spaces → underscores.

import { useCallback, useState } from 'react';
import {
  renderMixedWav,
  renderVoiceOnlyWav,
  triggerDownload,
} from '@/lib/audioExport';
import {
  exportTimelineVideo,
  hasVideoExportSupport,
  VideoExportCancelledError,
  type VideoExportInput,
} from '@/lib/videoExport';
import { videoInputSize as apiVideoInputSize } from '@/lib/api';
import { snapshotExportInput } from '@/lib/exportSnapshot';
import { ensureVideoDurations } from '@/lib/probeVideoDuration';
import { selectAllAssets, useSessionStore } from '@/stores/session';

/** Cloud-Video-Dauer nachladen, bevor der Snapshot die Export-Länge festlegt
 *  (Audit H6) — sonst fällt sie nach einem Reload auf targetDuration/30 s. */
async function ensureAssetVideoDuration(assetIdx: number): Promise<void> {
  const s = useSessionStore.getState();
  await ensureVideoDurations(s.toolId, selectAllAssets(s), [assetIdx]);
}

// ─── Shared helpers ─────────────────────────────────────────────────────────

function exportFilenameBase(
  assetIdx: number,
  title: string | undefined,
  suffix: string,
  ext: 'wav' | 'mp4'
): string {
  const safeTitle = (title || 'Untitled')
    .replace(/[^\w\d\s-]+/g, '')
    .trim()
    .replace(/\s+/g, '_');
  const num = String(assetIdx + 1).padStart(2, '0');
  return `${num}_${safeTitle}_${suffix}.${ext}`;
}

// ─── Core hook ─────────────────────────────────────────────────────────────

export type ExportKind = 'voice' | 'mixed' | 'video';

export interface ExportState {
  busy: boolean;
  kind: ExportKind | null;
  status: string;
  /** Nicht-fataler Hinweis des letzten Exports (bisher: Video-Ton fehlt im
   *  Premix, Audit M18). Die ExportBar zeigt ihn als Toast — der
   *  Status-Text allein verschwindet nach 4 s. */
  warning: string | null;
}

export function useExport() {
  const [state, setState] = useState<ExportState>({
    busy: false,
    kind: null,
    status: '',
    warning: null,
  });

  const guard = useCallback(
    async <T>(kind: ExportKind, fn: () => Promise<T>): Promise<T | null> => {
      if (state.busy) return null;
      setState({ busy: true, kind, status: '', warning: null });
      try {
        const result = await fn();
        return result;
      } finally {
        // Leave the terminal status for callers to clear after a short
        // timeout. Always release `busy` so the next export can run.
        setState((prev) => ({ ...prev, busy: false }));
      }
    },
    [state.busy]
  );

  const clearStatus = useCallback(() => {
    setState({ busy: false, kind: null, status: '', warning: null });
  }, []);

  const exportVoiceOnly = useCallback(
    async (assetIdx: number) => {
      await guard('voice', async () => {
        await ensureAssetVideoDuration(assetIdx);
        const { exportInput, assetTitle, effectiveType } = snapshotExportInput(assetIdx);
        if (!exportInput.totalDuration) {
          setState((p) => ({ ...p, status: 'No timeline duration' }));
          return;
        }
        if (!exportInput.voiceClips.length) {
          const msg = effectiveType === 'sts' ? 'No clips to export' : 'No favorites to export';
          setState((p) => ({ ...p, status: msg }));
          return;
        }
        setState((p) => ({ ...p, status: 'Rendering audio…' }));
        try {
          const blob = await renderVoiceOnlyWav(exportInput);
          const filename = exportFilenameBase(
            assetIdx,
            assetTitle,
            'VOICE-ONLY',
            'wav'
          );
          setState((p) => ({ ...p, status: 'Saving…' }));
          triggerDownload(blob, filename);
          setState({ busy: false, kind: 'voice', status: `Downloaded ${filename}`, warning: null });
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          setState({ busy: false, kind: 'voice', status: `Failed: ${msg}`, warning: null });
        }
      });
    },
    [guard]
  );

  const exportMixed = useCallback(
    async (assetIdx: number) => {
      await guard('mixed', async () => {
        await ensureAssetVideoDuration(assetIdx);
        const { exportInput, assetTitle, effectiveType } = snapshotExportInput(assetIdx);
        if (!exportInput.totalDuration) {
          setState((p) => ({ ...p, status: 'No timeline duration' }));
          return;
        }
        if (!exportInput.voiceClips.length) {
          const msg = effectiveType === 'sts' ? 'No clips to export' : 'No favorites to export';
          setState((p) => ({ ...p, status: msg }));
          return;
        }
        setState((p) => ({ ...p, status: 'Rendering audio…' }));
        try {
          const warnings: string[] = [];
          const blob = await renderMixedWav({
            ...exportInput,
            onWarning: (t) => warnings.push(t),
          });
          const filename = exportFilenameBase(
            assetIdx,
            assetTitle,
            'AUDIO-PREMIX',
            'wav'
          );
          setState((p) => ({ ...p, status: 'Saving…' }));
          triggerDownload(blob, filename);
          setState({
            busy: false,
            kind: 'mixed',
            status: `Downloaded ${filename}`,
            warning: warnings[0] ?? null,
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          setState({ busy: false, kind: 'mixed', status: `Failed: ${msg}`, warning: null });
        }
      });
    },
    [guard]
  );

  const exportVideoMp4 = useCallback(
    async (assetIdx: number) => {
      await guard('video', async () => {
        if (!hasVideoExportSupport()) {
          setState((p) => ({
            ...p,
            status: 'Video export not supported',
          }));
          return;
        }
        await ensureAssetVideoDuration(assetIdx);
        const { exportInput, assetTitle, videoSrc, videoBlob, videoPath, toolId } =
          snapshotExportInput(assetIdx);
        if (!videoSrc) {
          setState((p) => ({ ...p, status: 'No video loaded' }));
          return;
        }
        if (!exportInput.totalDuration) {
          setState((p) => ({ ...p, status: 'No timeline duration' }));
          return;
        }

        // Determine the input size so the pipeline can choose between
        // in-memory ArrayBufferTarget and streaming StreamTarget. 0 heißt
        // unbekannt (HEAD gescheitert) — dann wählt `chooseVideoOutput` den
        // Save-Dialog statt blind im Speicher zu muxen.
        let inputSize = 0;
        if (videoBlob) {
          inputSize = videoBlob.size;
        } else if (toolId && videoPath) {
          inputSize = (await apiVideoInputSize(toolId, videoPath)) ?? 0;
        }

        const suggestedFilename = exportFilenameBase(
          assetIdx,
          assetTitle,
          'VIDEO-PREMIX',
          'mp4'
        );

        const warnings: string[] = [];
        const videoInput: VideoExportInput = {
          // Blob vor URL — fetch auf blob:-URLs blockt die CSP (openVideoSource).
          videoSrc: videoBlob ?? videoSrc,
          inputSize,
          suggestedFilename,
          audio: { ...exportInput, onWarning: (t) => warnings.push(t) },
          onStatus: (status) =>
            setState((p) => ({ ...p, status })),
        };

        try {
          setState((p) => ({ ...p, status: 'Preparing export…' }));
          await exportTimelineVideo(videoInput);
          setState({
            busy: false,
            kind: 'video',
            status: `Saved ${suggestedFilename}`,
            warning: warnings[0] ?? null,
          });
        } catch (err) {
          if (err instanceof VideoExportCancelledError) {
            setState({ busy: false, kind: 'video', status: 'Cancelled', warning: null });
            return;
          }
          const msg = err instanceof Error ? err.message : String(err);
          setState({ busy: false, kind: 'video', status: `Failed: ${msg}`, warning: null });
        }
      });
    },
    [guard]
  );

  return {
    ...state,
    exportVoiceOnly,
    exportMixed,
    exportVideoMp4,
    clearStatus,
  };
}
